import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { tokenStore } from "../../api/tokenStore";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * Handoff codes are single-use by design, and React 18 StrictMode invokes an
 * effect twice on mount in development. Without this guard the second run
 * redeems an already-spent code, gets a 401, and signs the user straight back
 * out, a login that works in production and fails locally. Module scope, not
 * a ref, so it survives a remount too.
 */
const redeemed = new Set();

/**
 * Landing page of the OAuth round trip.
 *
 * The backend no longer puts tokens in this URL, it sends a one-time handoff
 * code instead, which we exchange over POST for the tokens themselves. A URL
 * ends up in browser history, in the Referer header of the next request, and
 * in every proxy log along the way; a request body does not.
 */
const LoginSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const code = searchParams.get("handoff");

    // Strip the code from the address bar before anything else runs. It is
    // single-use and short-lived, but there is no reason for it to sit in
    // history at all.
    window.history.replaceState({}, "", "/loginsuccess");

    if (!code) {
      // No code, but we already hold a session, a re-render after the
      // exchange, or a bookmarked /loginsuccess. Signing the user out here
      // would be gratuitous.
      if (tokenStore.getAccess()) {
        navigate("/dashboard", { replace: true });
        return;
      }
      window.location.href = `${API_BASE}/logout`;
      return;
    }

    const abandon = () => {
      tokenStore.clear();
      window.location.href = `${API_BASE}/logout`;
    };

    if (redeemed.has(code)) return;
    redeemed.add(code);

    (async () => {
      try {
        // withCredentials matters: the backend checks this code against the
        // session cookie it set in the same response, so the exchange only
        // works from the browser that actually signed in.
        const { data } = await axios.post(
          `${API_BASE}/api/auth/handoff`,
          { code },
          { withCredentials: true }
        );
        const tokens = data?.data ?? data;
        if (!tokens?.accessToken) {
          abandon();
          return;
        }
        tokenStore.set(tokens.accessToken, tokens.refreshToken);

        const profile = await fetchUser();
        if (profile) {
          navigate("/dashboard", { replace: true });
        } else {
          // Signed in with WSO2 but no usable ARMF profile, not our session
          // to keep.
          abandon();
        }
      } catch {
        setFailed(true);
        abandon();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-white">
      <div className="text-center">
        {!failed && (
          <div className="spinner-border text-success mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
        <h3 className="fw-bold">
          {failed ? "Couldn't finish signing you in" : "Authenticating..."}
        </h3>
        <p className="text-muted">
          {failed
            ? "Taking you back to sign in."
            : "Setting up your secure session"}
        </p>
      </div>
    </div>
  );
};

export default LoginSuccess;
